import React, { useEffect, useState } from 'react';
import Plot from 'react-plotly.js';
import './Plots.css';

const PowerEnergyCharts = ({ userId, experimentId, scenarioId }) => {
    const [resultsData, setResultsData] = useState(null);
    const [error, setError] = useState(null);

    const API_BASE_URL = "http://localhost:5001";

    useEffect(() => {
        if (!userId || !experimentId || !scenarioId) {
            console.error('Missing required parameters for PowerEnergyCharts:', { userId, experimentId, scenarioId });
            return;
        }

        setError(null);

        const fetchAllData = async () => {
            try {

                const resultsUrl = `${API_BASE_URL}/users/${userId}/experiments/${experimentId}/${scenarioId}/results`;
                const scenarioUrl = `${API_BASE_URL}/users/${userId}/experiments/${experimentId}/${scenarioId}/scenario`;


                // Fetch results data
                const resultsResponse = await fetch(resultsUrl);
                if (!resultsResponse.ok) throw new Error(`Failed to load results data: ${resultsResponse.status} ${resultsResponse.statusText}`);
                const resultsData = await resultsResponse.json();
                if (!resultsData.vehicles) throw new Error('No vehicles data found in results');

                // Fetch scenario data
                const scenarioResponse = await fetch(scenarioUrl);
                if (!scenarioResponse.ok) throw new Error(`Failed to load scenario data: ${scenarioResponse.status} ${scenarioResponse.statusText}`);
                const scenarioData = await scenarioResponse.json();

                // Set both pieces of data at once
                setResultsData({
                    ...resultsData,
                    scenarioInfo: scenarioData
                });
            } catch (err) {
                console.error('Error loading data:', err);
                setError(err.message);
            }
        };

        fetchAllData();
    }, [userId, experimentId, scenarioId]);

    const getPlotData = () => {
        if (!resultsData?.vehicles || !resultsData?.scenarioInfo) {
            console.log('Missing data for plots:', {
                hasVehicles: !!resultsData?.vehicles,
                hasScenarioInfo: !!resultsData?.scenarioInfo,
                userId,
                experimentId,
                scenarioId
            });
            return { powerData: null, energyData: null, chargerData: null };
        }

     

        const { vehicles, scenarioInfo } = resultsData;
        const deltaT = (scenarioInfo.granularity_minutes || 15) / 60;

        // sort timestamps chronologically
        const firstVehicle = Object.values(vehicles)[0];
        const timestamps = Object.keys(firstVehicle).sort((a, b) => {
            const [ah, am] = a.split(':').map(Number);
            const [bh, bm] = b.split(':').map(Number);
            return (ah * 60 + am) - (bh * 60 + bm);
        });

        // build ISO‐date array for plotting
        const baseDate = '2025-06-09';
        const xAsIso = timestamps.map(ts => `${baseDate}T${ts}:00`);

        // power trace
        const totalPowerByTimestamp = timestamps.map((ts, idx) => {
            let sum = 0;
            Object.values(vehicles).forEach(bus => {
                const curr = bus[ts];
                const prev = idx > 0 ? bus[timestamps[idx - 1]] : null;
                if (curr?.charger_id && prev) {
                    const diff = curr.energy_kWh - prev.energy_kWh;
                    if (diff > 0) sum += diff / deltaT;
                }
            });
            return sum;
        });

        const powerTraces = [{
            x: xAsIso,
            y: totalPowerByTimestamp,
            type: 'scatter',
            mode: 'lines+markers',
            name: 'Total Power (kW)',
            hovertemplate: '%{x|%H:%M} → %{y:.1f} kW<extra></extra>',
            line: { shape: 'spline' },
        }];

        // charger utilization segments
        const chargerTraces = [];
        const colors = [
            '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
            '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'
        ];
        const usedLegend = new Set();

        Object.entries(vehicles).forEach(([vehicleId, vehicleData]) => {
            let currentCharger = null;
            let startIdx = null;

            timestamps.forEach((ts, idx) => {
                const entry = vehicleData[ts];
                if (entry.charger_id !== currentCharger) {
                    if (currentCharger && startIdx !== null) {
                        chargerTraces.push({
                            x: [xAsIso[startIdx], xAsIso[idx]],
                            y: [currentCharger, currentCharger],
                            type: 'scatter',
                            mode: 'lines',
                            line: {
                                width: 20,
                                color: colors[parseInt(vehicleId.match(/\d+/)[0], 10) % colors.length]
                            },
                            name: vehicleId,
                            showlegend: false,
                            hoverinfo: 'text',
                            text: [
                                `${vehicleId} on ${currentCharger} from ${timestamps[startIdx]} to ${ts}`
                            ]
                        });
                        usedLegend.add(vehicleId);
                    }
                    if (entry.charger_id) {
                        currentCharger = entry.charger_id;
                        startIdx = idx;
                    } else {
                        currentCharger = null;
                        startIdx = null;
                    }
                }
            });

            if (currentCharger && startIdx !== null) {
                const lastIdx = timestamps.length - 1;
                chargerTraces.push({
                    x: [xAsIso[startIdx], xAsIso[lastIdx]],
                    y: [currentCharger, currentCharger],
                    type: 'scatter',
                    mode: 'lines',
                    line: {
                        width: 20,
                        color: colors[parseInt(vehicleId.match(/\d+/)[0], 10) % colors.length]
                    },
                    name: vehicleId,
                    showlegend: !usedLegend.has(vehicleId),
                    hoverinfo: 'text',
                    text: [
                        `${vehicleId} on ${currentCharger} from ${timestamps[startIdx]} to ${timestamps[lastIdx]}`
                    ]
                });
            }
        });

        // energy traces
        const energyTraces = Object.entries(vehicles).map(([vehicleId, vehicleData]) => {
            const vehicleType = vehicleId.includes('_') ? vehicleId.split('_')[1] : vehicleId;
    
            // Get the battery capacity from metadata
            let batteryCapacity = 100;
            if (resultsData.metadata && resultsData.metadata.vehicles) {
                // Loop through vehicle types in metadata to find a match
                Object.entries(resultsData.metadata.vehicles).forEach(([type, data]) => {
                    // Check if this metadata entry matches our vehicle type
                    if (type.includes(vehicleType) || vehicleType.includes(type)) {
                        if (data.battery_capacity) {
                            batteryCapacity = data.battery_capacity;
                        }
                    }
                });
            }
    
                
            // Calculate SoC for each timestamp
            const soc = timestamps.map(ts => {
                // Make sure energy_kWh exists for this timestamp
                if (vehicleData[ts] && typeof vehicleData[ts].energy_kWh === 'number') {
                    // Calculate SoC as percentage (0-100%)
                    return (vehicleData[ts].energy_kWh / batteryCapacity) * 100;
                }
                return null; // Return null for missing data points
            });    
            return {
                x: xAsIso,
                y: soc,
                type: 'scatter',
                mode: 'lines+markers',
                name: vehicleId,
                hovertemplate: '%{x|%H:%M} → %{y:.1f}%<extra></extra>',
                line: { shape: 'spline' },
            };
        });

        return {
            powerData: { data: powerTraces },
            chargerData: { data: chargerTraces },
            energyData: { data: energyTraces },
        };
    };

    const plotConfig = {
        responsive: true,
        displayModeBar: true,
        displaylogo: false,
        modeBarButtonsToRemove: ['lasso2d', 'select2d']
    };

    const commonXaxis = {
        title: 'Time [HH:mm]',
        type: 'date',
        tickangle: -45,
        automargin: true,
        tickformat: '%H:%M',
        tickfont: { size: 12 }
    };

    const powerLayout = {
        title: 'Total Power Purchased Over Time',
        xaxis: commonXaxis,
        yaxis: { title: 'Power [kW]', rangemode: 'tozero' },
        margin: { t: 50, b: 80, l: 60, r: 30 },
        showlegend: true,
        height: 400,
    };

    const energyLayout = {
        title: 'Vehicle State of Charge Over Time',
        xaxis: commonXaxis,
        yaxis: { title: 'SoC [%]', range: [0, 100] },
        legend: { orientation: 'h', y: -0.2 },
        margin: { t: 50, b: 100, l: 60, r: 30 },
        showlegend: true,
        height: 400,
    };

    const chargerLayout = {
        title: 'Charger Utilization Over Time',
        xaxis: commonXaxis,
        yaxis: { title: 'Charger ID', type: 'category', automargin: true },
        legend: { orientation: 'h', y: -0.2 },
        margin: { t: 50, b: 100, l: 60, r: 30 },
        showlegend: true,
        height: 400
    };

    if (error) {
        return <div style={{ color: 'red', padding: '1rem' }}>Error: {error}</div>;
    }

    // Show loading state if data is not ready
    if (!resultsData?.vehicles || !resultsData?.scenarioInfo) {
        return <div style={{ textAlign: 'center', padding: '2rem' }}>Loading plots data...</div>;
    }

    const { powerData, chargerData, energyData } = getPlotData() || {};

    return (
        <div className="charts-container">
            <div className="charts-row">
                <div className="chart-column">
                    <h2>Total Power Purchased Over Time</h2>
                    {powerData && powerData.data && (
                        <Plot
                            data={powerData.data}
                            layout={powerLayout}
                            config={plotConfig}
                            className="chart"
                        />
                    )}
                </div>

                <div className="chart-column">
                    <h2>Vehicle State of Charge Over Time</h2>
                    {energyData && energyData.data && (
                        <Plot
                            data={energyData.data}
                            layout={energyLayout}
                            config={plotConfig}
                            className="chart"
                        />
                    )}
                </div>
            </div>

            <div className="charts-row single-chart">
                <div className="chart-column full-width">
                    <h2>Charger Utilization Over Time</h2>
                    {chargerData && chargerData.data && (
                        <Plot
                            data={chargerData.data}
                            layout={chargerLayout}
                            config={plotConfig}
                            className="chart"
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default PowerEnergyCharts;
